import {
  partPricesQuery,
  useDeletePartPriceMutation,
} from "@common/api/part-prices"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { formatCurrency } from "@common/lib/currency"
import { formatDate } from "@common/lib/date-utils"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { priceKindLabel } from "@/routes/_layout/inventory/-lib/prices"
import { conditionLabel } from "@/routes/_layout/inventory/-lib/stock"

export const Route = createFileRoute(
  "/_layout/inventory/$partId/prices/$priceId/delete",
)({
  loader: ({ context: { queryClient }, params: { partId } }) =>
    queryClient.ensureQueryData(partPricesQuery(partId)),
  component: DeletePriceDialog,
})

function DeletePriceDialog(): React.ReactElement {
  const { partId, priceId } = Route.useParams()
  const { data } = useSuspenseQuery(partPricesQuery(partId))
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const deleteMutation = useDeletePartPriceMutation()
  const price = data.data.find((candidate) => candidate.id === priceId)
  const description = price
    ? [
        conditionLabel(price.condition),
        priceKindLabel(price.kind).toLowerCase(),
        `price of ${formatCurrency(price.amount, price.currency)}`,
        `from ${formatDate(price.priced_at)} will be archived.`,
      ].join(" ")
    : "This price will be archived."

  const close = () =>
    navigate({
      to: "/inventory/$partId",
      params: { partId },
      search: true,
    })

  const confirm = () =>
    deleteMutation.mutate(
      { price_id: priceId },
      {
        onSuccess: () => {
          showToast("Archived", "Price archived.", "success")
          close()
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title="Archive this price?"
      description={description}
      action="Archive"
      tone="default"
      pendingAction="Archiving…"
      pending={deleteMutation.isPending}
      onConfirm={confirm}
      onCancel={close}
    />
  )
}
